import { useState, useEffect, useCallback } from 'react';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { ScaleData, Point } from '@/types/floorPlanViewer';
import { 
  pixelsToUnits, 
  unitsToPixels, 
  formatMeasurement, 
  calculatePolygonArea, 
  formatArea,
  snapToGrid as snapToGridUtil
} from '@/utils/scaleCalculations';
import { logger } from '@/lib/logger';

export function useFloorPlanScale(floorPlanId: string) {
  const [scaleData, setScaleData] = useState<ScaleData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch scale data from database
  const fetchScaleData = useCallback(async () => {
    if (!floorPlanId) {
      setIsLoading(false);
      return;
    }

    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase
        .from('floor_plans')
        .select('scale_data')
        .eq('id', floorPlanId)
        .maybeSingle();

      if (error) {
        logger.error('Error fetching scale data:', error);
      } else if (data?.scale_data) {
        setScaleData(data.scale_data as ScaleData);
      }
    } catch (err) {
      logger.error('Exception fetching scale data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [floorPlanId]);

  useEffect(() => {
    fetchScaleData();
    
    // Listen for scale updates from other instances
    const handleScaleUpdate = (event: CustomEvent) => {
      if (event.detail.floorPlanId === floorPlanId) {
        setScaleData(event.detail.scaleData);
      }
    };
    
    window.addEventListener('scaleDataUpdated', handleScaleUpdate as EventListener);
    return () => {
      window.removeEventListener('scaleDataUpdated', handleScaleUpdate as EventListener);
    };
  }, [floorPlanId]);

  // Convert pixels to real units
  const pixelsToUnitsCallback = useCallback((pixels: number): number => {
    return pixelsToUnits(pixels, scaleData);
  }, [scaleData]);

  // Convert units to pixels
  const unitsToPixelsCallback = useCallback((units: number): number => {
    return unitsToPixels(units, scaleData);
  }, [scaleData]);

  // Format measurement for display
  const formatMeasurementCallback = useCallback((pixels: number): string => {
    return formatMeasurement(pixels, scaleData);
  }, [scaleData]);

  // Calculate and format area
  const calculateArea = useCallback((points: Point[]): string => {
    const pixelArea = calculatePolygonArea(points);
    return formatArea(pixelArea, scaleData);
  }, [scaleData]);

  // Update scale data
  const updateScale = useCallback(async (newScaleData: ScaleData) => {
    // Update local state immediately for optimistic update
    setScaleData(newScaleData);

    // Save to database
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { error } = await supabase
        .from('floor_plans')
        .update({
          scale_data: newScaleData
        })
        .eq('id', floorPlanId);

      if (error) {
        logger.error('Error updating scale data:', error);
        // Revert on error
        fetchScaleData();
        return false;
      }

      logger.log('Scale data updated successfully');
      
      // Emit custom event to notify other instances of the hook
      window.dispatchEvent(new CustomEvent('scaleDataUpdated', { 
        detail: { floorPlanId, scaleData: newScaleData } 
      }));
      
      return true;
    } catch (err) {
      logger.error('Exception updating scale data:', err);
      // Revert on error
      fetchScaleData();
      return false;
    }
  }, [floorPlanId, fetchScaleData]);

  // Snap to grid functionality
  const snapToGrid = useCallback((point: Point, gridSize: number = 1): Point => {
    return snapToGridUtil(point, gridSize, scaleData);
  }, [scaleData]);

  // Check if scale is available
  const hasScale = Boolean(scaleData);

  return {
    scaleData,
    isLoading,
    hasScale,
    pixelsToUnits: pixelsToUnitsCallback,
    unitsToPixels: unitsToPixelsCallback,
    formatMeasurement: formatMeasurementCallback,
    calculateArea,
    updateScale,
    snapToGrid,
    refetch: fetchScaleData
  };
}
